import { PlotReportFactElement, R1FactType } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';

export const useFactWidget = ({
  factType,
  headerText,
  tooltipText,
  formatter,
}: {
  factType: R1FactType;
  headerText: string;
  tooltipText?: string;
  formatter?: (fact?: PlotReportFactElement<unknown>) => PlotReportFactElement<unknown> | undefined;
}) => {
  const data = useOrganizationIndicatorReportingFact(factType);
  const { [factType]: factData } = data;

  const formattedFact = formatter ? formatter(factData) : factData;

  const hasData = !isNullish(formattedFact?.value);

  const dataDisplay = hasData
    ? `${getDisplayNumber(formattedFact?.value as string, window.navigator.language)} ${formatUnit(UnitEnum[formattedFact?.unit ?? 'none'])}`
    : '--';

  return {
    name: DefaultWidgetNames.default_asset_fact_card,
    type: WidgetType.o_card,
    visible: hasData,
    relatedData: {
      styles: {
        root: 'flex-1 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      },
    },
    widgets: [
      {
        name: `${factType}_header`,
        type: WidgetType.a_text,
        relatedData: {
          className: 'flex items-center gap-1',
        },
        visible: true,
        widgets: [
          {
            name: `${factType}_header_text`,
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: headerText,
              className: 'typography-body1 text-text-primary line-clamp-2',
            },
          },
          {
            name: `${factType}_header_tooltip`,
            type: WidgetType.m_tooltip,
            visible: !!tooltipText,
            relatedData: {},
            widgets: [
              {
                name: `${factType}_header_tooltip_trigger`,
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: `${factType}_header_tooltip_trigger_icon`,
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-2 text-active-54',
                    },
                  },
                ],
              },
              {
                name: `${factType}_header_tooltip_content`,
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: `${factType}_header_tooltip_text`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: { i18nPath: tooltipText, className: 'whitespace-pre-wrap' },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: `${factType}_value`,
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: dataDisplay,
          className: 'typography-h2 text-text-primary',
        },
      },
    ],
  } as WidgetSlotConfiguration;
};
